<template>
  <div class="p-0">
    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Licencias y permisos</h1>
        <p class="mt-1 text-sm text-gray-600">Gestioná tus solicitudes, borradores y correcciones pendientes.</p>
        <router-link to="/leave/balances" class="inline-flex items-center mt-2 text-sm font-semibold text-indigo-700 hover:text-indigo-900">
          Mis cupos <i class="fa-solid fa-arrow-right ml-2"></i>
        </router-link>
      </div>

      <router-link
        to="/leave/requests/new"
        class="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800 shadow-sm"
      >
        <i class="fa-solid fa-plus mr-2"></i>
        Nueva solicitud
      </router-link>
    </div>

    <nav v-if="isManager" class="module-tabs">
      <router-link to="/leave/inbox" class="module-tab">Bandeja de revision</router-link>
      <router-link to="/leave/requests" class="module-tab module-tab-active">Mis solicitudes</router-link>
      <router-link to="/leave/calendar" class="module-tab">Calendario</router-link>
      <router-link to="/leave/admin/articles" class="module-tab">Administracion</router-link>
    </nav>

    <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-5">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label class="block text-xs font-semibold text-gray-500 mb-1">Estado</label>
          <select v-model="filters.status" @change="fetchRequests" class="field">
            <option v-for="status in statuses" :key="status.value" :value="status.value">
              {{ status.label }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 mb-1">Desde</label>
          <input v-model="filters.from" @change="fetchRequests" type="date" class="field" />
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 mb-1">Hasta</label>
          <input v-model="filters.to" @change="fetchRequests" type="date" class="field" />
        </div>
        <div class="flex items-end">
          <button
            type="button"
            @click="clearFilters"
            class="w-full px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Limpiar filtros
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
    </div>

    <div v-else-if="requests.length === 0" class="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
      <div class="mx-auto w-14 h-14 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center mb-4">
        <i class="fa-regular fa-clipboard text-2xl"></i>
      </div>
      <h2 class="text-lg font-semibold text-gray-900">Todavía no tenés solicitudes</h2>
      <p class="mt-1 text-sm text-gray-500">Cuando inicies una licencia o permiso, aparecerá en este listado.</p>
      <router-link
        to="/leave/requests/new"
        class="inline-flex items-center mt-5 px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800"
      >
        + Nueva solicitud
      </router-link>
    </div>

    <div v-else class="space-y-4">
      <article
        v-for="request in requests"
        :key="request.id || request.request_id"
        class="bg-white border border-gray-200 rounded-xl shadow-sm p-4 hover:border-indigo-200 transition-colors"
      >
        <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div class="min-w-0">
            <div class="flex flex-wrap gap-2 items-center">
              <h2 class="text-lg font-semibold text-gray-900 truncate">{{ requestTitle(request) }}</h2>
              <span class="px-2 py-1 text-xs font-semibold rounded-full border" :class="statusClass(request.status)">
                {{ statusLabel(request.status) }}
              </span>
            </div>
            <p class="mt-1 text-sm text-gray-600">
              {{ dateRange(request) }}
              <span v-if="requestedDays(request)" class="font-semibold text-gray-800"> · {{ requestedDays(request) }}</span>
            </p>
            <p class="mt-2 text-xs text-gray-500">
              {{ secondaryDate(request) }}
            </p>
            <div v-if="requestObservation(request)" class="mt-3 bg-orange-50 border border-orange-100 text-orange-800 rounded-md px-3 py-2 text-sm">
              <i class="fa-solid fa-circle-exclamation mr-2"></i>
              {{ requestObservation(request) }}
            </div>
          </div>

          <div class="flex flex-wrap justify-end gap-2">
            <button
              v-if="canEditRequest(request)"
              @click="editRequest(request)"
              class="px-3 py-2 text-sm font-semibold text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100"
            >
              {{ request.status === 'draft' ? 'Continuar' : 'Corregir' }}
            </button>
            <button
              @click="openRequest(request)"
              class="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Ver detalle
            </button>
          </div>
        </div>
      </article>

      <p class="text-center text-xs text-gray-500">Mostrando {{ requests.length }} solicitudes</p>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import { leaveRequestsApi } from '@/service/leave.service';
import {
  LEAVE_STATUSES,
  canEditRequest,
  formatDate,
  formatDateTime,
  isLeaveManagerRole,
  requestTitle,
  statusClass,
  statusLabel,
  unwrapList,
} from '@/utils/leave';

export default {
  name: 'LeaveRequestsView',
  components: { Loading },
  data() {
    return {
      loading: false,
      error: '',
      requests: [],
      filters: {
        status: '',
        from: '',
        to: '',
      },
      statuses: LEAVE_STATUSES,
    };
  },
  computed: {
    currentUserId() {
      return this.$store.getters['authentication/currentUser']?.user_id;
    },
    isManager() {
      return isLeaveManagerRole(this.$store.getters['authentication/currentUser']?.role);
    },
  },
  created() {
    this.fetchRequests();
  },
  methods: {
    statusClass,
    statusLabel,
    requestTitle,
    canEditRequest,
    async fetchRequests() {
      if (!this.currentUserId) return;
      this.loading = true;
      this.error = '';
      try {
        const response = await leaveRequestsApi.list({
          user_id: this.currentUserId,
          status: this.filters.status,
          from: this.filters.from,
          to: this.filters.to,
        });
        this.requests = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar tus solicitudes.';
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = { status: '', from: '', to: '' };
      this.fetchRequests();
    },
    requestId(request) {
      return request.id || request.request_id;
    },
    openRequest(request) {
      this.$router.push(`/leave/requests/${this.requestId(request)}`);
    },
    editRequest(request) {
      this.$router.push(`/leave/requests/${this.requestId(request)}/edit`);
    },
    dateRange(request) {
      const start = formatDate(request.start_date);
      const end = formatDate(request.end_date);
      return start === end ? start : `${start} → ${end}`;
    },
    requestedDays(request) {
      const days = request.requested_days ?? request.days ?? request.total_days;
      if (!days) return '';
      return `${days} ${Number(days) === 1 ? 'día' : 'días'}`;
    },
    secondaryDate(request) {
      if (request.approved_at) return `Aprobada: ${formatDateTime(request.approved_at)}`;
      if (request.rejected_at) return `Rechazada: ${formatDateTime(request.rejected_at)}`;
      if (request.cancelled_at) return `Cancelada: ${formatDateTime(request.cancelled_at)}`;
      if (request.created_at) return `Creada: ${formatDateTime(request.created_at)}`;
      return 'Sin fecha de creación';
    },
    requestObservation(request) {
      return request.observation || request.observation_reason || request.last_observation || request.message;
    },
  },
};
</script>

<style scoped>
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.module-tabs {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-1 mb-5 inline-flex flex-wrap gap-1;
}
.module-tab {
  @apply px-4 py-2 text-sm font-semibold text-gray-600 rounded-md hover:bg-gray-50;
}
.module-tab-active {
  @apply bg-indigo-900 text-white hover:bg-indigo-900;
}

/*
 * @apply bakes fixed light-mode colors into these scoped classes, so the
 * global dark-mode retrofit in style.css (which only overrides classes
 * literally present in the DOM) can never reach them. Same story for a few
 * status/observation badge colors below that the retrofit doesn't cover
 * for anyone.
 */
.dark .field {
  @apply bg-gray-700 border-gray-600 text-gray-100;
}
.dark .module-tabs {
  @apply bg-gray-800 border-gray-700;
}
.dark .module-tab {
  @apply text-gray-300;
}
.dark .module-tab:hover {
  @apply bg-gray-700;
}

.dark .bg-yellow-100 {
  background-color: rgba(234, 179, 8, 0.2);
}
.dark .bg-orange-100 {
  background-color: rgba(249, 115, 22, 0.2);
}
.dark .bg-blue-100 {
  background-color: rgba(59, 130, 246, 0.2);
}
.dark .text-yellow-800 {
  color: theme('colors.yellow.300');
}
.dark .text-orange-800 {
  color: theme('colors.orange.300');
}
.dark .text-blue-800 {
  color: theme('colors.blue.300');
}
.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.35);
}
.dark .border-green-200 {
  border-color: rgba(34, 197, 94, 0.35);
}
.dark .border-yellow-200 {
  border-color: rgba(234, 179, 8, 0.35);
}
.dark .border-orange-100,
.dark .border-orange-200 {
  border-color: rgba(249, 115, 22, 0.35);
}
.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.35);
}
.dark .hover\:bg-indigo-100:hover {
  background-color: rgba(99, 102, 241, 0.25);
}
.dark .hover\:border-indigo-200:hover {
  border-color: rgba(99, 102, 241, 0.5);
}
.dark .hover\:text-indigo-900:hover {
  color: theme('colors.indigo.200');
}
</style>
